import React, { useState, useEffect, useRef, useMemo } from 'react';
import { db } from '../firebase';
import { ref, onValue, query, orderByChild, startAt } from 'firebase/database';
import GameRow from './GameRow';
import MobileCard from './MobileCard';
import PartidoDetalleModal from './modals/PartidoDetalleModal';
import EmptyState from './EmptyState';
import { HeroBall } from './EEBrand';
import { isLive, matchesLeague, isEarlyGame, isNoCoverage, normalizeLeague } from '../utils';
// FASE V7 — hook para leer /live_games (schema oficial del bot con
// v6.categoria, categoria_dashboard, razon_humana). Se mergea con /picks
// (legacy) para enriquecer cada pick con la categoría calculada por el bot.
import { useLiveGames } from '../hooks/useLiveGames';
import { useActiveSignals } from '../hooks/useActiveSignals';
import MotorStatusCard from './MotorStatusCard';
import MotorInfoCard from './MotorInfoCard';

function AgeBadge({ timestamp }) {
  if (!timestamp) return null;
  const secs = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
  const color = secs < 15 ? 'var(--pos)' : secs < 30 ? 'var(--warn)' : 'var(--neg)';
  const label = secs < 10 ? 'ahora' : secs < 60 ? `${secs}s` : `${Math.floor(secs/60)}m`;
  return <span style={{ fontSize:9, fontWeight:600, color, background:color+'18', padding:'1px 5px', borderRadius:4, marginLeft:4 }}>{label}</span>;
}

function groupByLeague(picks) {
  const groups = {};
  picks.forEach(p => { const liga = p.liga||'Otros'; if (!groups[liga]) groups[liga]=[]; groups[liga].push(p); });
  return Object.entries(groups).sort(([,a],[,b]) => b.filter(p=>(p.decision||'').toLowerCase().includes('fuerte')).length - a.filter(p=>(p.decision||'').toLowerCase().includes('fuerte')).length);
}

// Calcula el veredicto efectivo. Categorías:
//   'enter'     → edge ≥ |8| + timing GO + stake > 0
//   'watch'     → edge entre |6| y |8|
//   'early'     → muestra insuficiente (< 15 min jugados o dataConf < 25%)
//   'wait'      → resto (esperar por edge bajo, trampas, etc.)
//   'no-cover'  → partido sin cobertura de línea (>= 2 min sin línea API)
//                  NO cuenta en KPIs ni es clickeable
function getEffectiveVerdict(p, lineValues) {
  // 0) HT/BT (entretiempo/break) → siempre ESPERAR
  //    El modelo es inestable en el entretiempo, por eso forzamos wait.
  const clockUpper = String(p.clock || '').toUpperCase().trim();
  if (['HT','BT','HALF','HALFTIME','BREAK'].includes(clockUpper)) return 'wait';

  // 1) Sin cobertura de línea (>= 2 min jugados y la API no entregó línea)
  if (isNoCoverage(p)) return 'no-cover';

  // 2) TEMPRANO: muestra insuficiente — máxima prioridad
  if (isEarlyGame(p)) return 'early';

  // 3) Trampas activas con stake 0 → ESPERAR
  const trampas = typeof p.trampas==='number' ? p.trampas : parseInt(p.trampas||'0',10);
  const stake   = parseFloat(String(p.stake||'0').match(/-?\d+(\.\d+)?/)?.[0]||'0');
  if (trampas > 0 && stake === 0) return 'wait';

  // 4) Si el usuario ingresó línea manual, usar ese edge (ya no se usa, pero queda por si acaso)
  const lv = lineValues[p.id];
  let edge;
  if (lv && lv.edge !== undefined) {
    edge = lv.edge;
  } else {
    edge = parseFloat(p.edge || 0);
  }

  // 5) Categorizar por edge real (mismo umbral que GameRow y DetailPanel)
  const canBeFuerte = (p.timing||'').toUpperCase() === 'GO' && stake > 0;
  if ((edge <= -8 || edge >= 8) && canBeFuerte) return 'enter';
  if (edge <= -6 || edge >= 6) return 'watch';
  return 'wait';
}

function KpiRow({ activeFilter, setFilter, liveGamesCounts }) {
  // FASE V7 — counts vienen de `liveGamesCounts` calculado sobre
  // byLeagueFiltered (mismo set que la tabla). Garantiza coherencia total:
  // suma de las 6 cards == filas visibles en la tabla.
  const enter      = liveGamesCounts.ENTRAR;
  const watch      = liveGamesCounts.VIGILAR;
  const wait       = liveGamesCounts.ESPERAR;
  const early      = liveGamesCounts.TEMPRANO;
  const soloInfo   = liveGamesCounts.SOLO_INFO;
  const descartado = liveGamesCounts.DESCARTADO;
  const toggle = k => setFilter(activeFilter===k?'all':k);
  const Spark = ({vals}) => <span className="spark">{vals.map((v,i)=><i key={i} style={{height:`${v}%`}}/>)}</span>;
  const Tick = () => <svg className="kpi-tick" viewBox="0 0 12 12" width="12" height="12"><path d="M2 6l3 3 5-6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>;
  // V6: 6 columnas (4 originales + 2 nuevas). Las 2 nuevas usan inline style
  // para color de punto (cyan / rojo) sin tocar CSS global.
  return (
    <div className="kpis kpis-6col" data-tour="kpi-row">
      <button className="kpi" data-tone="enter" data-tour="kpi-enter" data-active={activeFilter==='enter'} onClick={()=>toggle('enter')} type="button">
        <div className="kpi-label"><span className="sq"/>Entrar {activeFilter==='enter'&&<Tick/>}</div>
        <div className="kpi-value">{enter}</div>
        <div className="kpi-foot"><span>{enter>0?'picks con señal fuerte':'sin señal · esperando'}</span><Spark vals={[30,45,35,60,70,55,80,95]}/></div>
      </button>
      <button className="kpi" data-tone="watch" data-active={activeFilter==='watch'} onClick={()=>toggle('watch')} type="button">
        <div className="kpi-label"><span className="sq"/>Vigilar {activeFilter==='watch'&&<Tick/>}</div>
        <div className="kpi-value">{watch}</div>
        <div className="kpi-foot"><span>{watch>0?'ventaja marginal · observar':'—'}</span><Spark vals={[60,40,50,65,45,55,60,70]}/></div>
      </button>
      <button className="kpi" data-tone="wait" data-active={activeFilter==='wait'} onClick={()=>toggle('wait')} type="button">
        <div className="kpi-label"><span className="sq"/>Esperar {activeFilter==='wait'&&<Tick/>}</div>
        <div className="kpi-value">{wait}</div>
        <div className="kpi-foot"><span>{wait>0?'edge insuficiente':'—'}</span><Spark vals={[45,35,40,30,40,35,30,40]}/></div>
      </button>
      <button className="kpi" data-tone="wait" data-active={activeFilter==='early'} onClick={()=>toggle('early')} type="button">
        <div className="kpi-label"><span className="sq"/>Temprano {activeFilter==='early'&&<Tick/>}</div>
        <div className="kpi-value">{early}</div>
        <div className="kpi-foot"><span>{early>0?'sin muestra suficiente':'sin partidos en fase inicial'}</span><Spark vals={[20,30,25,30,35,25,30,20]}/></div>
      </button>
      <button className="kpi" data-active={activeFilter==='solo_info'} onClick={()=>toggle('solo_info')} type="button" style={{ '--kpi-c': '#06B6D4' }}>
        <div className="kpi-label"><span className="sq"/>Solo info {activeFilter==='solo_info'&&<Tick/>}</div>
        <div className="kpi-value">{soloInfo}</div>
        <div className="kpi-foot"><span>{soloInfo>0?'no recomendado por motor':'—'}</span><Spark vals={[25,30,20,35,28,32,26,30]}/></div>
      </button>
      <button className="kpi" data-active={activeFilter==='descartado'} onClick={()=>toggle('descartado')} type="button" style={{ '--kpi-c': 'var(--neg)' }}>
        <div className="kpi-label"><span className="sq"/>Descartado {activeFilter==='descartado'&&<Tick/>}</div>
        <div className="kpi-value">{descartado}</div>
        <div className="kpi-foot"><span>{descartado>0?'fuera de cobertura del motor':'—'}</span><Spark vals={[15,20,18,22,16,19,14,17]}/></div>
      </button>
    </div>
  );
}

export default function LivePage({ picks, connected, selectedLeague, user, userData, tick }) {
  const [selectedId, setSelectedId] = useState(null);
  const [lineValues, setLineValues] = useState({});
  const [activeFilter, setActiveFilter] = useState('all');
  // Filtro por cuarto activo: 'all' | '1' | '2' | '3' | '4'
  const [periodFilter, setPeriodFilter] = useState('all');
  // Filtro por deporte (2026-09-04): solo se muestra si hay handball en vivo,
  // para no agregar ruido en la vista de básquet.
  const [sportFilter, setSportFilter] = useState('all');
  // FASE V7 — listener real-time a /live_games (categorías + razones del bot).
  const { games: liveGames, byId: liveGamesById, loading: liveLoading, error: liveError } = useLiveGames();
  // Set de pick_ids que tienen señal ACTIVA en /signals_history/
  // (para mostrar contador en KPI y badge visual, NO para filtrar la lista)
  const [signalActivePickIds, setSignalActivePickIds] = useState(new Set());
  // Map<pick_id, { line, started_at }> con la línea al momento del envío
  // a Telegram. signals_tracker la graba como `line` en signals_history.
  // Sirve para la sub-pill "📩 TG enviada en X" del dashboard.
  const [tgSentLines, setTgSentLines] = useState(new Map());
  const alertedIdsRef = useRef(new Set());
  const audioCtxRef = useRef(null);
  const soundEnabledRef = useRef(false);
  // Nombre: prioriza campo "nombre" del doc Firestore users/{uid}, despues displayName
  // del user de Firebase Auth, y por ultimo fallback a localStorage / 'Trader'.
  const fullName = userData?.nombre || user?.displayName || localStorage.getItem('mee_name') || 'Trader';
  const name = String(fullName).split(' ')[0];

  // Listener: traer señales activas del historial
  // Solo se usa para CONTAR cuántos partidos tienen señal activa (KPI),
  // NO para filtrar la lista de partidos del dashboard.
  useEffect(() => {
    // Ventana reciente (~6h) por started_at (indexado) en vez del nodo completo
    // de 5.2MB. Cubre de sobra la duración de un partido (≤3h) + el orphan-closer
    // cierra toda señal !ended_at a los ~14min. Sirve ambos consumidores del
    // callback SIN cambiar su lógica: signalActivePickIds (todas las activas,
    // incl. no-TG, vía !ended_at) y tgSentLines (vía telegram_sent===true).
    // Las shadow tienen started_at=null → fuera de la ventana, pero hoy tampoco
    // encienden badge (su pick_id no es un gid) → mismo resultado visible.
    const sigRef = query(ref(db, 'signals_history'), orderByChild('started_at'), startAt(Date.now() - 6 * 60 * 60 * 1000));
    const unsub = onValue(sigRef, snap => {
      const data = snap.val() || {};
      const activeIds = new Set();
      const lineMap = new Map();
      Object.values(data).forEach(sig => {
        if (sig && typeof sig === 'object' && sig.pick_id) {
          const pid = String(sig.pick_id);
          const lineNum = parseFloat(sig.line);
          // Guardamos línea de envío incluso si el signal ya cerró:
          // sirve para mostrar "TG enviada en X" mientras el partido
          // siga visible en la tabla (post-cierre del signal pero pre-FT).
          // 2026-05-21: SOLO señales realmente enviadas al VIP (telegram_sent===true).
          // Antes mostraba "TG enviada" para señales del método viejo / shadow que
          // NO se enviaban → indicador falso. Ahora solo las reales (EG1) lo muestran.
          if (sig.telegram_sent === true && Number.isFinite(lineNum) && lineNum > 0) {
            const prev = lineMap.get(pid);
            // Si hay múltiples envíos para un mismo pick_id, nos quedamos
            // con el más antiguo (la primera alerta que rompió anti-spam).
            if (!prev || (sig.started_at && prev.started_at && sig.started_at < prev.started_at)) {
              lineMap.set(pid, { line: lineNum, started_at: sig.started_at || null });
            }
          }
          if (!sig.ended_at) activeIds.add(pid);
        }
      });
      setSignalActivePickIds(activeIds);
      setTgSentLines(lineMap);
    });
    return () => unsub();
  }, []);

  // FASE V7 — enriquecer cada pick con datos de /live_games (v6, categoria_dashboard,
  // razon_humana). Si el bot ya escribió ese partido a /live_games, prevalece su
  // categoría sobre la lógica local del cliente. Si no, queda como antes.
  const enrichedPicks = useMemo(() => {
    if (!liveGamesById || liveGamesById.size === 0) return picks;
    return picks.map((p) => {
      const live = liveGamesById.get(String(p.id));
      if (!live) return p;
      return {
        ...p,
        v6: live.v6,
        categoria_dashboard: live.categoria_dashboard,
        razon_humana: live.v6?.razon_humana,
      };
    });
  }, [picks, liveGamesById]);

  const activePicks = enrichedPicks.filter(isLive);
  const byLeagueFiltered = activePicks.filter(p => matchesLeague(p, selectedLeague));

  // Mapa gid → pick para el hook useActiveSignals (decide activa vs registrada
  // comparando snapshot.linea contra pick.linea live).
  const picksByGid = useMemo(() => {
    const m = new Map();
    for (const p of enrichedPicks) {
      if (p && p.id != null) m.set(String(p.id), p);
    }
    return m;
  }, [enrichedPicks]);
  const { active: activeSignals, recorded: recordedSignals } = useActiveSignals(picksByGid);

  // Debug: si el filtro vacía todo aunque haya picks en vivo, dejar
  // un warning con muestra de ligas para diagnosticar mismatch de
  // strings (encoding, espacios invisibles, etc.). Solo cuando el
  // selectedLeague NO es 'all' y había partidos antes del filtro.
  useEffect(() => {
    if (
      selectedLeague &&
      selectedLeague !== 'all' &&
      activePicks.length > 0 &&
      byLeagueFiltered.length === 0
    ) {
      // eslint-disable-next-line no-console
      console.warn('[Filter Debug] liga sin matches', {
        selectedLeague,
        selectedLeagueNorm: normalizeLeague(selectedLeague),
        activePicks: activePicks.length,
        sampleLeagues: activePicks.slice(0, 5).map((p) => ({
          raw: p.liga,
          norm: normalizeLeague(p.liga),
        })),
      });
    }
  }, [selectedLeague, activePicks.length, byLeagueFiltered.length]);

  // FASE V7 — counts por categoria_dashboard real del bot.
  // FIX (cards-coherentes-con-tabla): contamos sobre `byLeagueFiltered`
  // (mismo conjunto que la tabla) en vez de `liveGames` (TODO /live_games).
  // Antes las cards mostraban los 73 partidos analizados por el bot,
  // pero la tabla solo mostraba el subset que matcheaba con /picks +
  // isLive + matchesLeague. Ahora ambos reflejan el mismo set.
  // Picks sin categoría server-side caen a 'ESPERAR' como default seguro
  // para garantizar que la suma de las 6 cards == filas de la tabla.
  const liveGamesCounts = useMemo(() => {
    const c = { ENTRAR: 0, VIGILAR: 0, ESPERAR: 0, TEMPRANO: 0, SOLO_INFO: 0, DESCARTADO: 0, _total: 0 };
    byLeagueFiltered.forEach((p) => {
      let cat = (p?.categoria_dashboard || p?.v6?.categoria || '').toUpperCase().replace(' ', '_');
      if (!(cat in c)) cat = 'ESPERAR';
      c[cat] += 1;
      c._total += 1;
    });
    return c;
  }, [byLeagueFiltered]);

  // Filtro por KPI: si el pick tiene categoria_dashboard del bot, la usamos
  // directamente. Si no, fallback a getEffectiveVerdict (legacy).
  const filteredByKpi = activeFilter==='all' ? byLeagueFiltered : byLeagueFiltered.filter(p => {
    if (p.categoria_dashboard) {
      const map = { enter: 'ENTRAR', watch: 'VIGILAR', wait: 'ESPERAR', early: 'TEMPRANO',
                    solo_info: 'SOLO_INFO', descartado: 'DESCARTADO' };
      return p.categoria_dashboard === map[activeFilter];
    }
    return getEffectiveVerdict(p, lineValues) === activeFilter;
  });

  // Contadores por período para los chips. Se calculan sobre `byLeagueFiltered`
  // (no `filteredByKpi`) para que los números no parpadeen al combinar filtros.
  const periodCounts = useMemo(() => {
    const c = { all: byLeagueFiltered.length, 1: 0, 2: 0, 3: 0, 4: 0 };
    byLeagueFiltered.forEach((p) => {
      const per = Number(p.period);
      if (per >= 1 && per <= 4) c[per] += 1;
    });
    return c;
  }, [byLeagueFiltered]);

  // Filtro por cuarto en cascada: aplica después del KPI.
  const _esHandball = (p) => p.match_type === 'handball'
    || /handball/i.test(String(p.liga || p.league || ''));
  const filteredByPeriod = periodFilter === 'all'
    ? filteredByKpi
    : filteredByKpi.filter((p) => Number(p.period) === Number(periodFilter));
  const hayHandball = activePicks.some(_esHandball);
  const filtered = sportFilter === 'all'
    ? filteredByPeriod
    : filteredByPeriod.filter((p) => (sportFilter === 'handball' ? _esHandball(p) : !_esHandball(p)));

  const [searchQuery, setSearchQuery] = useState('');

  const grouped = groupByLeague(filtered.filter(p => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (p.partido||'').toLowerCase().includes(q) ||
           (p.liga||'').toLowerCase().includes(q) ||
           (p.away_team||'').toLowerCase().includes(q) ||
           (p.home_team||'').toLowerCase().includes(q);
  }));

  useEffect(()=>{
    function enableAudio(){
      if(!audioCtxRef.current){try{audioCtxRef.current=new(window.AudioContext||window.webkitAudioContext)();soundEnabledRef.current=true;}catch(e){}}
      if('Notification'in window&&Notification.permission==='default') Notification.requestPermission();
    }
    document.addEventListener('click',enableAudio,{once:true});
    return ()=>document.removeEventListener('click',enableAudio);
  },[]);

  function playAlert(){
    const ctx=audioCtxRef.current; if(!ctx||!soundEnabledRef.current) return;
    try{
      const o1=ctx.createOscillator(),g1=ctx.createGain(); o1.frequency.value=880; o1.type='sine';
      g1.gain.setValueAtTime(0,ctx.currentTime); g1.gain.linearRampToValueAtTime(0.3,ctx.currentTime+0.02); g1.gain.exponentialRampToValueAtTime(0.001,ctx.currentTime+0.3);
      o1.connect(g1).connect(ctx.destination); o1.start(ctx.currentTime); o1.stop(ctx.currentTime+0.3);
      const o2=ctx.createOscillator(),g2=ctx.createGain(); o2.frequency.value=1320; o2.type='sine';
      g2.gain.setValueAtTime(0,ctx.currentTime+0.15); g2.gain.linearRampToValueAtTime(0.3,ctx.currentTime+0.17); g2.gain.exponentialRampToValueAtTime(0.001,ctx.currentTime+0.5);
      o2.connect(g2).connect(ctx.destination); o2.start(ctx.currentTime+0.15); o2.stop(ctx.currentTime+0.5);
    }catch(e){}
  }

  useEffect(()=>{
    activePicks.forEach(p=>{
      // MODO CONSERVADOR: solo alertar si Firebase dice "fuerte" explícitamente
      const d=(p.decision||'').toLowerCase();
      if(!d.includes('fuerte')){alertedIdsRef.current.delete(p.id);return;}

      const stake=parseFloat(String(p.stake||'0').match(/-?\d+(\.\d+)?/)?.[0]||'0');
      const linea=p.linea??p.line??p.over_under??0;

      // Requiere stake > 0 y línea válida
      if(stake<=0||!linea||parseFloat(linea)<=0){alertedIdsRef.current.delete(p.id);return;}

      if(alertedIdsRef.current.has(p.id)) return;
      alertedIdsRef.current.add(p.id);
      playAlert();

      if('Notification'in window&&Notification.permission==='granted'){
        const side=(p.decision||'').toUpperCase().includes('UNDER')?'UNDER':'OVER';
        const edge=p.edge?`${p.edge>0?'+':''}${parseFloat(p.edge).toFixed(1)}`:'';
        try{new Notification(`🔔 ${side} ★ VIP — ${p.partido||'?'}`,{body:`Línea: ${linea}  ·  Edge: ${edge}  ·  Stake: ${p.stake}`,icon:'/favicon.ico',tag:`vip-${p.id}`,requireInteraction:true});}catch(e){}
      }
    });
  },[activePicks]);

  return (
    <div className="page">
      <header className="header-greeting">
        <h1 className="page-title page-title-greeting" style={{display:'flex', alignItems:'center', gap:6, flexWrap:'wrap'}}>
          <span style={{display:'inline-flex', alignItems:'center', gap:0}}>
            Hola, {name}<span style={{fontSize:26, marginLeft:2}}>👋</span>
          </span>
          <HeroBall size={32} />
        </h1>
        <div className="page-sub subtitle">
          <div className="greeting-line-1">Bienvenido a tu centro de operaciones</div>
          <div className="greeting-line-2">
            {activePicks.length === 0 ? (
              'Sin partidos en vivo por ahora'
            ) : (
              <>
                <span className="greeting-live-dot" aria-hidden="true" />
                {activePicks.length} partido{activePicks.length !== 1 ? 's' : ''} en vivo
              </>
            )}
          </div>
        </div>
      </header>

      <div data-tour="motor-card">
        <MotorInfoCard />
      </div>

      <MotorStatusCard
        games={liveGames}
        activeSignals={activeSignals}
        recordedSignals={recordedSignals}
      />

      {activeFilter!=='all'&&(
        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12,fontSize:12,color:'var(--muted)'}}>
          <span>Filtrando: <strong style={{color:'var(--ink)'}}>{activeFilter==='enter'?'ENTRAR':activeFilter==='watch'?'VIGILAR':activeFilter==='wait'?'ESPERAR':'TEMPRANO'}</strong></span>
          <button onClick={()=>setActiveFilter('all')} style={{background:'transparent',border:'1px solid var(--line)',borderRadius:6,padding:'3px 10px',fontSize:11,cursor:'pointer',color:'var(--muted)',fontFamily:'inherit'}}>✕</button>
        </div>
      )}

      {/* Filtro por cuarto */}
      {hayHandball && (
        <div className="period-chips" role="tablist" aria-label="Filtro por deporte" style={{ marginBottom: 8 }}>
          {[
            { key: 'all', label: 'Todos' },
            { key: 'basquet', label: '🏀 Básquet' },
            { key: 'handball', label: '🤾 Handball' },
          ].map(({ key, label }) => {
            const n = key === 'all' ? filteredByPeriod.length
              : filteredByPeriod.filter((p) => (key === 'handball' ? _esHandball(p) : !_esHandball(p))).length;
            const active = sportFilter === key;
            return (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={active}
                className={`period-chip ${active ? 'is-active' : ''}`}
                onClick={() => setSportFilter(key)}
              >
                <span className="period-chip-label">{label}</span>
                <span className="period-chip-count">{n}</span>
              </button>
            );
          })}
        </div>
      )}

      <div className="period-chips" role="tablist" aria-label="Filtro de cuarto">
        {[
          { key: 'all', label: 'Todos' },
          { key: '1',   label: 'Q1' },
          { key: '2',   label: 'Q2' },
          { key: '3',   label: 'Q3' },
          { key: '4',   label: 'Q4' },
        ].map(({ key, label }) => {
          const count = periodCounts[key];
          const active = periodFilter === key;
          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={active}
              className={`period-chip ${active ? 'is-active' : ''}`}
              onClick={() => setPeriodFilter(key)}
            >
              <span className="period-chip-label">{label}</span>
              <span className="period-chip-count">{count}</span>
            </button>
          );
        })}
      </div>

      {filtered.length===0 ? (
        liveError && (!liveGames || liveGames.length === 0) ? (
          // FASE V7 — error leyendo /live_games (y sin /picks legacy)
          <div style={{
            background: 'var(--bg-elev)', border: '1px solid var(--line)',
            borderLeft: '3px solid var(--neg)',
            borderRadius: 16, padding: '36px 28px', textAlign: 'center',
            boxShadow: 'var(--shadow-sm)'
          }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink)', marginBottom: 6 }}>
              No pudimos conectar con el motor en vivo
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 18, lineHeight: 1.6 }}>
              Hubo un problema leyendo /live_games desde Firebase. Verifica tu conexión y reintenta.
            </div>
            <button onClick={() => window.location.reload()} style={{
              background: 'var(--ink)', color: 'var(--bg-elev)', border: 'none',
              borderRadius: 8, padding: '10px 20px', fontSize: 13,
              fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            }}>
              Reintentar
            </button>
          </div>
        ) : (liveLoading && !connected) ? (
          // FASE V7 — skeleton mientras carga el primer snapshot
          <div className="panel" style={{ padding: 24 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[0,1,2].map(i => (
                <div key={i} style={{
                  height: 72, borderRadius: 10,
                  background: 'linear-gradient(90deg, var(--bg-subtle) 0%, var(--bg-elev) 50%, var(--bg-subtle) 100%)',
                  backgroundSize: '200% 100%',
                  animation: 'skeleton-shimmer 1.4s ease-in-out infinite',
                }}/>
              ))}
            </div>
          </div>
        ) : activeFilter !== 'all' ? (
          // Hay picks pero ninguno en esta categoría
          <div style={{
            background: 'var(--bg-elev)', border: '1px solid var(--line)',
            borderRadius: 16, padding: '48px 32px', textAlign: 'center',
            boxShadow: 'var(--shadow-sm)'
          }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>
              {activeFilter === 'enter' ? '🟢' : activeFilter === 'watch' ? '🟡' : activeFilter === 'wait' ? '⏸' : '⏳'}
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--ink)', marginBottom: 8 }}>
              No hay señales {activeFilter === 'enter' ? 'ENTRAR' : activeFilter === 'watch' ? 'VIGILAR' : activeFilter === 'wait' ? 'ESPERAR' : 'TEMPRANO'} en este momento
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20, lineHeight: 1.6 }}>
              {byLeagueFiltered.length > 0
                ? `Hay ${byLeagueFiltered.length} partido${byLeagueFiltered.length !== 1 ? 's' : ''} en vivo en otras categorías.`
                : 'El motor está escaneando — aparecerá cuando haya edge.'}
            </div>
            <button onClick={() => setActiveFilter('all')} style={{
              background: 'var(--ink)', color: 'var(--bg-elev)', border: 'none',
              borderRadius: 8, padding: '10px 20px', fontSize: 13,
              fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            }}>
              Ver todos los partidos
            </button>
          </div>
        ) : (connected || !liveLoading) ? <EmptyState/> : liveError ? (
          // RTDB rechazó (permission_denied / cuota): error visible, nunca
          // spinner eterno. El caso con games ya lo cubre la rama de arriba.
          <div className="no-data" style={{ color: 'var(--neg)' }}>
            ⚠️ No pudimos conectar con el motor en vivo.{' '}
            <a onClick={() => window.location.reload()} style={{ textDecoration: 'underline', cursor: 'pointer' }}>Reintentar</a>
          </div>
        ) : <div className="no-data">Conectando con Firebase...</div>
      ) : (
        <>
          {/* DESKTOP TABLE */}
          <div className="panel" data-tour="games-section">
            <div className="panel-head">
              <div className="title">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>
                <span>Basketball en vivo</span>
                <span className="hint">· clic en una fila para ver el detalle</span>
              </div>
              <div className="panel-tools">
                {/* Buscador */}
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    style={{ position: 'absolute', left: 9, color: 'var(--muted)', pointerEvents: 'none' }}>
                    <circle cx="11" cy="11" r="7"/><path d="M21 21l-4-4"/>
                  </svg>
                  <input
                    type="text"
                    placeholder="Buscar partido..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    style={{
                      paddingLeft: 28, paddingRight: searchQuery ? 28 : 10,
                      paddingTop: 6, paddingBottom: 6,
                      fontSize: 12, border: '1px solid var(--line)',
                      borderRadius: 7, background: 'var(--bg-subtle)',
                      color: 'var(--ink)', outline: 'none',
                      width: 160, fontFamily: 'inherit',
                      transition: 'border-color .15s, width .2s',
                    }}
                    onFocus={e => { e.target.style.borderColor = 'var(--accent)'; e.target.style.width = '200px'; }}
                    onBlur={e => { e.target.style.borderColor = 'var(--line)'; e.target.style.width = '160px'; }}
                  />
                  {searchQuery && (
                    <button onClick={() => setSearchQuery('')} aria-label="Limpiar búsqueda" style={{
                      position: 'absolute', right: 7,
                      background: 'none', border: 'none',
                      cursor: 'pointer', color: 'var(--muted)',
                      fontSize: 14, lineHeight: 1, padding: 0,
                    }}>×</button>
                  )}
                </div>
                <button className="btn" title="Refrescar" aria-label="Refrescar">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 11a8 8 0 1 0-2.3 5.7M20 4v5h-5"/></svg>
                </button>
                <button className="btn" title="Más opciones" aria-label="Más opciones">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="5" cy="12" r="1.2"/><circle cx="12" cy="12" r="1.2"/><circle cx="19" cy="12" r="1.2"/></svg>
                </button>
              </div>
            </div>
            <table className="matches">
              <thead>
                <tr>
                  <th>Partido</th>
                  <th className="num">Marcador</th>
                  <th className="num">Línea O/U</th>
                  <th className="num">Edge</th>
                  <th>Veredicto</th>
                </tr>
              </thead>
              <tbody>
                {grouped.map(([liga,ligaPicks], gIdx)=>(
                  <React.Fragment key={liga}>
                    <tr className="group-row" data-tour={gIdx === 0 ? 'league-header' : undefined}><td colSpan={5}><span className="league"><span className="dot"/>{liga}</span><span style={{marginLeft:10,color:'var(--muted-2)',textTransform:'none',letterSpacing:0,fontSize:11.5}}>{ligaPicks.length} {ligaPicks.length===1?'partido':'partidos'}</span></td></tr>
                    {ligaPicks.map((p, pIdx)=>(
                      <GameRow
                        key={p.id}
                        pick={p}
                        selected={selectedId===p.id}
                        onClick={()=>setSelectedId(p.id)}
                        ageBadge={<AgeBadge timestamp={p.timestamp}/>}
                        tick={tick}
                        isLast={false}
                        onLineChange={(line,edge)=>setLineValues(prev=>({...prev,[p.id]:{line,edge}}))}
                        tgSent={signalActivePickIds.has(String(p.id))}
                        tgLine={tgSentLines.get(String(p.id))?.line ?? null}
                        dataTour={gIdx === 0 && pIdx === 0 ? 'game-row' : undefined}
                      />
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* MOBILE CARDS */}
          <div className="mobile-cards">
            {grouped.map(([liga,ligaPicks], gIdx)=>(
              <div key={liga} style={{marginBottom:4}}>
                <div style={{display:'flex',alignItems:'center',gap:6,padding:'8px 4px 4px',flexWrap:'wrap'}}>
                  <div style={{width:6,height:6,borderRadius:'50%',background:'var(--accent)'}}/>
                  <span style={{fontSize:10,fontWeight:700,color:'var(--muted)',letterSpacing:'.04em',textTransform:'uppercase'}}>{liga}</span>
                  <span style={{fontSize:9,color:'var(--muted-2)'}}>{ligaPicks.length} partido{ligaPicks.length!==1?'s':''}</span>
                </div>
                {ligaPicks.map((p, pIdx)=>(
                  <MobileCard
                    key={p.id}
                    pick={p}
                    selected={selectedId===p.id}
                    onClick={()=>setSelectedId(p.id)}
                    tick={tick}
                    onLineChange={(line,edge)=>setLineValues(prev=>({...prev,[p.id]:{line,edge}}))}
                    tgSent={signalActivePickIds.has(String(p.id))}
                    tgLine={tgSentLines.get(String(p.id))?.line ?? null}
                    dataTour={gIdx === 0 && pIdx === 0 ? 'game-row' : undefined}
                  />
                ))}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Modal flotante de detalle del partido. Reemplaza el panel
          desplegable inline (rompía el flujo visual de la tabla y
          forzaba scroll para volver al partido seleccionado). */}
      <PartidoDetalleModal
        pick={selectedId ? picks.find(p => p.id === selectedId) : null}
        onClose={() => setSelectedId(null)}
        lineOverride={selectedId ? lineValues[selectedId] : undefined}
      />
    </div>
  );
}
